import { useEffect, useState } from "react";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-public";

export type ApAccountingSettings = components["schemas"]["ApAccountingSettingsResponse"];

/**
 * Whether invoices can be pushed to the books; false until known. Pass `enabled` only once `ar.accounting` is held:
 * receivables-settings 403s, and toasts "not included in your plan", without it.
 */
export function useArAccountingConnected(enabled: boolean): boolean {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    setConnected(false);
    if (!enabled || !organisationId) return;
    let cancelled = false;
    void api.GET("/v1/accounting/receivables-settings", {}).then(({ data }) => {
      if (!cancelled) setConnected(data?.connected ?? false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, organisationId, enabled]);

  return connected;
}

/** Payables accounting settings while a platform is connected, else null. Same `enabled` rule, for `ap.accounting`. */
export function useApAccountingSettings(enabled: boolean): ApAccountingSettings | null {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const [settings, setSettings] = useState<ApAccountingSettings | null>(null);

  useEffect(() => {
    setSettings(null);
    if (!enabled || !organisationId) return;
    let cancelled = false;
    void api.GET("/v1/accounting/payables-settings", {}).then(({ data }) => {
      if (!cancelled) setSettings(data?.connected ? data : null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, organisationId, enabled]);

  return settings;
}
